package com.eladfinish.bible.contest.platform.shake

import kotlinx.browser.window
import org.w3c.dom.events.Event
import kotlin.js.Date
import kotlin.math.sqrt

/**
 * Detects a shake from the browser's `devicemotion` events, with the thresholds of Android's
 * ShakeDetector. Only phones and tablets send those events; on a desktop browser this never fires.
 *
 * iOS Safari sends them only after the user has allowed motion access, which it lets a page ask
 * for only from a tap — so the request is made on the first tap after [start], if still needed.
 */
class WebShakeController : ShakeController {

    private var onShake: (() -> Unit)? = null
    private var lastShakeMillis = 0.0

    private val motionListener: (Event) -> Unit = listener@{ event ->
        val acceleration = event.asDynamic().accelerationIncludingGravity ?: return@listener
        val x = (acceleration.x as? Number)?.toDouble() ?: return@listener
        val y = (acceleration.y as? Number)?.toDouble() ?: return@listener
        val z = (acceleration.z as? Number)?.toDouble() ?: return@listener

        // Close to 1 when the device is at rest.
        val gForce = sqrt(x * x + y * y + z * z) / GRAVITY_EARTH
        if (gForce > SHAKE_THRESHOLD_GRAVITY) {
            val now = Date.now()
            if (lastShakeMillis + SHAKE_SLOP_TIME_MS <= now) {
                lastShakeMillis = now
                onShake?.invoke()
            }
        }
    }

    private val permissionListener: (Event) -> Unit = {
        removePermissionListener()
        try {
            js("DeviceMotionEvent").requestPermission()
        } catch (_: Throwable) {
        }
    }

    override fun start(onShake: () -> Unit) {
        if (js("typeof DeviceMotionEvent === 'undefined'").unsafeCast<Boolean>()) return
        stop()
        this.onShake = onShake
        window.addEventListener("devicemotion", motionListener)
        if (js("typeof DeviceMotionEvent.requestPermission === 'function'").unsafeCast<Boolean>()) {
            window.addEventListener("pointerup", permissionListener)
        }
    }

    override fun stop() {
        onShake = null
        window.removeEventListener("devicemotion", motionListener)
        removePermissionListener()
    }

    private fun removePermissionListener() {
        window.removeEventListener("pointerup", permissionListener)
    }

    private companion object {
        const val GRAVITY_EARTH = 9.80665
        const val SHAKE_THRESHOLD_GRAVITY = 2.7
        const val SHAKE_SLOP_TIME_MS = 500
    }
}
